<script setup>
import {reactive,onMounted} from 'vue';
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {Permissions} from "@/Utils/Permissions";
import {activeComponentStore} from "@/Stores/ActiveComponentStore";
import {ToastUtil} from "@/Utils/ToastUtil";

const compName = 'UserStatusComponent';

const props = defineProps({
  contextSchemaA: String,
  contextIdA: String
});

const data = reactive({
  addFee: false,
  comment: '',
  isDisabledComment: false,
  isDisabledFee: false,
  isDisabledTrainer: false,
  items: [
    {
      title: 'Active',
    },
    {
      title: 'Inactive'
    },
    {
      title: 'Rejected'
    }
  ],
  isEditing: false,
  isEditingFee: false,
  loading: true,
  loadingComment: false,
  loadingFee: false,
  loadingTrainer: false,
  preferred: null,
  showFee: false,
  showSubmitComment: false,
  showTrainerName: false,
  status: null,
  names: [],
  trainer: null,
  training_fee: null,
  catactive: null,
  firstcall: null,
  messageEmails: null,
})

const setFirstCall = () => {
  data.loading = true;
  ApiWrapper.post('user-card', 'set-first-call', {
    personId: props.contextIdA,
    firstcall: data.firstcall
  })
  .then((response) => {
    getStatus();
    ToastUtil.successMsg('Updated first call');
  })
  .catch((error) => {
    ToastUtil.error(error);
    data.loading = false
  })
}

const setCatActive = () => {
  data.loading = true;

  ApiWrapper.post('user-card', 'set-cat-active', {
    personId: props.contextIdA,
    catactive: data.catactive
  })
  .then((response) => {
    getStatus();
    ToastUtil.successMsg('Updated active for cat');
  })
  .catch((error) => {
    ToastUtil.error(error);
    data.loading = false
  })
}

const getStatus = () => {
  data.loading = true
  ApiWrapper.post('user-card', 'get-status', {
    personId: props.contextIdA
  })
  .then((response) => {
    if(response.data){
      data.status = response.data.status
      data.preferred = response.data.preferred
      data.firstcall = response.data.firstcall
      data.comment = response.data.shortcomment
      data.catactive = response.data.catactive
      data.trainer = response.data.trainer
      data.training_fee = response.data.training_fee
      data.messageEmails = response.data.email_all_messages
    }
    if(response.people){
      let trainerNames = response.people.map(function (obj) {
        return obj.name
      })
      data.names = trainerNames.sort()
    }
  })
  .catch((error) => {
    ToastUtil.error(error);
    data.loading = false
  })
  .finally(()=>{
    data.loading = false
  });
}

const setStatus = (item) => {
  data.loading = true;
  ApiWrapper.post('user-card', 'set-status', {
    personId: props.contextIdA,
    status: item.title
  })
  .then((response) => {
    getStatus();
    activeComponentStore.notifyComponents(['certificationsList'],compName);
    ToastUtil.successMsg('Updated status');
  })
  .catch((error) => {
    ToastUtil.error(error);
    data.loading = false
  })
}

const setPreferred = (item) => {
  data.loading = true;
  ApiWrapper.post('user-card', 'set-preferred', {
    personId: props.contextIdA,
    preferred: data.preferred
  })
  .then((response) => {
    getStatus();
    ToastUtil.successMsg('Updated preferred');
  })
  .catch((error) => {
    ToastUtil.error(error);
    data.loading = false
  })
}

const setMessageEmails = () => {
  data.loading = true;

  ApiWrapper.post('user-card', 'set-message-emails', {
    personId: props.contextIdA,
    bool: data.messageEmails
  })
  .then((response) => {
    getStatus();
    ToastUtil.successMsg('Updated "Receiving Emails for Messages"');
  })
  .catch((error) => {
    ToastUtil.error(error);
    data.loading = false
  })
}

const submitComment = () => {
  data.loadingComment = true
  data.isDisabledComment = true

  data.loading = true;

  ApiWrapper.post('user-card', 'submit-comment', {
    personId: props.contextIdA,
    comment: data.comment
  })
  .then((response) => {
    getStatus();
    data.showSubmitComment = false
    data.loadingComment = false
    data.isDisabledComment = false
    ToastUtil.successMsg('Updated comment');
  })
  .catch((error) => {
    ToastUtil.error(error);
    data.loading = false;
    data.loadingComment = false
    data.isDisabledComment = false
  })
}

const submitTrainer = () => {
  data.loading = true
  data.showTrainerName = true
  data.addFee = true
  ApiWrapper.post('user-card', 'submit-trainer', {
    personId: props.contextIdA,
    trainer: data.trainer
  })
  .then((response) => {
    getStatus();
    data.loadingTrainer = false
    data.isDisabledTrainer = false
    ToastUtil.successMsg('Updated trainer');
  })
  .catch((error) => {
    ToastUtil.error(error);
    data.loading = false;
    data.loadingTrainer = false;
    data.isDisabledTrainer = false
  })
}

const submitFee = () => {
  data.loading = true
  data.isDisabledFee = true
  data.addFee = true
  ApiWrapper.post('user-card', 'submit-fee', {
    personId: props.contextIdA,
    fee: data.training_fee.toString()
  })
  .then((response) => {
    getStatus();
    data.isDisabledFee = false
    data.addFee = false
    ToastUtil.successMsg('Updated training fee');
  })
  .catch((error) => {
    ToastUtil.error(error);
    data.loading = false;
    data.isDisabledFee = false;
    data.addFee = false;
  })
}

onMounted(()=>{
  getStatus()
})
</script>

<template>
  <v-container fluid>
    <v-card>
      <v-toolbar flat color="white">
        <v-toolbar-title>User Status</v-toolbar-title>
        <v-progress-linear :active="data.loading" :indeterminate="data.loading" absolute bottom color="primary">
        </v-progress-linear>
        <v-divider class="mx-4" inset vertical></v-divider>

        <v-spacer></v-spacer>
        <v-menu offset-y>
          <template v-slot:activator="{ on, props }">
            <v-btn color="primary" dark v-bind="props" v-on="on">
              Change Status
            </v-btn>
          </template>
          <v-list>
            <v-list-item v-for="(item, index) in data.items" :key="index" @click="setStatus(item)">
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-toolbar>

      <v-row align="center" class="px-5">
        <v-col align="center">
            <h2>Status: {{data.status}}</h2>
            <v-divider insert></v-divider>
            <v-checkbox @change="setPreferred" label="Preferred" v-model="data.preferred" :disabled="!Permissions.checkPermissions(['users.set-preferred-status'])"> </v-checkbox>
            <v-checkbox @change="setFirstCall" label="First Call" v-model="data.firstcall"> </v-checkbox>
            <v-checkbox @change="setCatActive" label="Active For Cat" v-model="data.catactive"> </v-checkbox>
            <v-checkbox @change="setMessageEmails" label="Receiving Emails For Messages" v-model="data.messageEmails"> </v-checkbox>


            <!-- Comment -->
            <v-textarea
                v-model="data.comment"
                auto-grow
                filled
                color="primary"
                label="Comment"
                rows="2"
                clearable
                clear-icon="mdi-close-circle"
                v-on:keyup="data.showSubmitComment = true"
            ></v-textarea>

              <v-card-actions v-if="data.showSubmitComment">
                <v-spacer></v-spacer>
                <v-btn
                    color="primary"
                    size="small"
                    variant="elevated"
                    :loading="data.loadingComment"
                    :disabled="data.isDisabledComment"
                    @click="submitComment">
                  Submit
                </v-btn>
                <v-btn
                    color="error"
                    size="small"
                    variant="elevated"
                    :disabled="data.isDisabledComment"
                    @click="data.showSubmitComment = false; data.comment = ''">
                  Cancel
                </v-btn>
              </v-card-actions>
            <!-- Add Trainer -->
            <v-autocomplete
                color="primary"
                v-model="data.trainer"
                :items="data.names"
                label="Type or Select Trainer's Name"
                :clearable="true"
                @update:modelValue="data.trainer=$event;submitTrainer()"
            ></v-autocomplete>


            <!-- Add Trainer Fee -->
            <v-list-item density="compact" class="px-0">
              <v-text-field
                  class="mr-2"
                  v-model="data.training_fee"
                  label="Add Training Fee"
                  hint="Please enter"
                  append-icon="mdi-percent"
                  @keyup.enter="submitFee();"
              ></v-text-field>
            </v-list-item>
        </v-col>
      </v-row>

    </v-card>
  </v-container>
</template>

<style scoped>
  .container {
    padding: 5px;
  }

  [v-cloak] {
    display: none;
  }
</style>